import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { 
  ShoppingCart, 
  Plus, 
  Minus, 
  Trash2, 
  Check, 
  ShoppingBag,
  Apple,
  Pill,
  Sparkles,
  Home,
  Shirt,
  Baby,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

// Category tabs
const categories = [
  { id: 'groceries', name: 'Groceries', icon: Apple },
  { id: 'health', name: 'Health & Pharmacy', icon: Pill },
  { id: 'cosmetics', name: 'Cosmetics', icon: Sparkles },
  { id: 'household', name: 'Household', icon: Home },
  { id: 'clothing', name: 'Clothing', icon: Shirt },
  { id: 'baby', name: 'Baby & Kids', icon: Baby },
];

// Products by category with 30% discount
const products = {
  groceries: [
    { id: 'g1', name: 'Fresh Milk 2L', originalPrice: 42, image: '🥛', stock: 50 },
    { id: 'g2', name: 'Brown Bread Loaf', originalPrice: 18, image: '🍞', stock: 100 },
    { id: 'g3', name: 'Free Range Eggs (12)', originalPrice: 58, image: '🥚', stock: 30 },
    { id: 'g4', name: 'Chicken Breast 1kg', originalPrice: 95, image: '🍗', stock: 25 },
    { id: 'g5', name: 'Fresh Vegetables Mix', originalPrice: 65, image: '🥬', stock: 40 },
    { id: 'g6', name: 'Basmati Rice 2kg', originalPrice: 72, image: '🍚', stock: 60 },
    { id: 'g7', name: 'Cooking Oil 2L', originalPrice: 85, image: '🫒', stock: 35 },
    { id: 'g8', name: 'Fresh Fruit Basket', originalPrice: 120, image: '🍎', stock: 20 },
    { id: 'g9', name: 'Cheese Block 500g', originalPrice: 89, image: '🧀', stock: 45 },
    { id: 'g10', name: 'Coffee Beans 500g', originalPrice: 145, image: '☕', stock: 30 },
  ],
  health: [
    { id: 'h1', name: 'Multivitamins (60 tabs)', originalPrice: 189, image: '💊', stock: 40 },
    { id: 'h2', name: 'Pain Relief Tablets', originalPrice: 65, image: '💊', stock: 100 },
    { id: 'h3', name: 'First Aid Kit', originalPrice: 250, image: '🩹', stock: 15 },
    { id: 'h4', name: 'Hand Sanitizer 500ml', originalPrice: 45, image: '🧴', stock: 80 },
    { id: 'h5', name: 'Face Masks (50 pack)', originalPrice: 120, image: '😷', stock: 50 },
  ],
  cosmetics: [
    { id: 'c1', name: 'Moisturizing Cream', originalPrice: 185, image: '🧴', stock: 30 },
    { id: 'c2', name: 'Lipstick Set', originalPrice: 295, image: '💄', stock: 25 },
    { id: 'c3', name: 'Perfume 100ml', originalPrice: 450, image: '🌸', stock: 20 },
    { id: 'c4', name: 'Shampoo & Conditioner', originalPrice: 145, image: '🧴', stock: 45 },
    { id: 'c5', name: 'Nail Polish Set', originalPrice: 120, image: '💅', stock: 35 },
  ],
  household: [
    { id: 'hh1', name: 'Laundry Detergent 3L', originalPrice: 165, image: '🧺', stock: 40 },
    { id: 'hh2', name: 'Dish Soap 1L', originalPrice: 45, image: '🧼', stock: 60 },
    { id: 'hh3', name: 'Toilet Paper (12 rolls)', originalPrice: 85, image: '🧻', stock: 50 },
    { id: 'hh4', name: 'Air Freshener Pack', originalPrice: 95, image: '🌺', stock: 45 },
    { id: 'hh5', name: 'Cleaning Supplies Set', originalPrice: 220, image: '🧹', stock: 25 },
  ],
  clothing: [
    { id: 'cl1', name: 'Cotton T-Shirt', originalPrice: 199, image: '👕', stock: 30 },
    { id: 'cl2', name: 'Denim Jeans', originalPrice: 450, image: '👖', stock: 20 },
    { id: 'cl3', name: 'Running Shoes', originalPrice: 850, image: '👟', stock: 15 },
    { id: 'cl4', name: 'Winter Jacket', originalPrice: 1200, image: '🧥', stock: 10 },
    { id: 'cl5', name: 'Socks (5 pairs)', originalPrice: 120, image: '🧦', stock: 50 },
  ],
  baby: [
    { id: 'b1', name: 'Baby Diapers (50 pack)', originalPrice: 320, image: '👶', stock: 35 },
    { id: 'b2', name: 'Baby Formula 900g', originalPrice: 285, image: '🍼', stock: 40 },
    { id: 'b3', name: 'Baby Wipes (200 pack)', originalPrice: 95, image: '🧻', stock: 60 },
    { id: 'b4', name: 'Baby Clothes Set', originalPrice: 350, image: '👕', stock: 20 },
    { id: 'b5', name: 'Educational Toys', originalPrice: 180, image: '🧸', stock: 25 },
  ],
};

export const MarketplaceSection = () => {
  const { user, api, refreshUser } = useAuth();
  const [activeCategory, setActiveCategory] = useState('groceries');
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [processing, setProcessing] = useState(false);

  const getDiscountedPrice = (originalPrice) => {
    return (originalPrice * 0.7).toFixed(2); // 30% off
  };

  const addToCart = (product) => {
    const existing = cart.find(item => item.id === product.id);
    if (existing) {
      if (existing.quantity >= product.stock) {
        toast.error('Maximum stock reached');
        return;
      }
      setCart(cart.map(item => 
        item.id === product.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
    toast.success(`${product.name} added to cart`);
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, delta) => {
    setCart(cart.map(item => {
      if (item.id === productId) {
        const newQty = item.quantity + delta;
        if (newQty <= 0) return null;
        if (newQty > item.stock) {
          toast.error('Maximum stock reached');
          return item;
        }
        return { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean));
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      return total + (parseFloat(getDiscountedPrice(item.originalPrice)) * item.quantity);
    }, 0);
  };

  const getTotalSavings = () => {
    return cart.reduce((total, item) => {
      return total + ((item.originalPrice * 0.3) * item.quantity);
    }, 0);
  };

  const handleCheckout = async () => {
    const total = getCartTotal();
    
    if (total > user?.main_balance) {
      toast.error('Insufficient funds. Please top up your account.');
      return;
    }

    setProcessing(true);
    try {
      // Process marketplace purchase
      await api.post('/marketplace/purchase', {
        items: cart.map(item => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          price: parseFloat(getDiscountedPrice(item.originalPrice))
        })),
        total: total
      });
      
      toast.success('Purchase successful! Your order is being processed.');
      setCart([]);
      setShowCart(false);
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purchase failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="space-y-6" data-testid="marketplace-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="font-syne text-2xl font-bold text-gray-900">Marketplace</h1>
        <Button
          onClick={() => setShowCart(true)}
          className="relative btn-primary"
          data-testid="open-cart-btn"
        >
          <ShoppingCart className="w-5 h-5 mr-2" />
          Cart
          {cartItemCount > 0 && (
            <span className="absolute -top-2 -right-2 w-6 h-6 bg-white text-afriperx-gold text-xs font-bold rounded-full flex items-center justify-center">
              {cartItemCount}
            </span>
          )}
        </Button>
      </div>

      {/* Discount Banner */}
      <Card className="card-raoul p-4 border-afriperx-gold/20 bg-gradient-to-r from-red-50 to-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-afriperx-green/20 flex items-center justify-center">
            <ShoppingBag className="w-5 h-5 text-afriperx-gold" />
          </div>
          <div>
            <p className="text-gray-900 font-medium">AfriPerx Member Exclusive: 30% OFF Everything!</p>
            <p className="text-gray-500 text-sm">Shop and pay directly from your AfriPerx balance</p>
          </div>
        </div>
      </Card>

      {/* Category Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {categories.map((category) => {
          const Icon = category.icon;
          const isActive = activeCategory === category.id;
          return (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                isActive 
                  ? 'bg-afriperx-green text-white' 
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
              data-testid={`category-${category.id}`}
            >
              <Icon className="w-4 h-4" />
              {category.name}
            </button>
          );
        })}
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {products[activeCategory]?.map((product) => {
          const discountedPrice = getDiscountedPrice(product.originalPrice);
          const inCart = cart.find(item => item.id === product.id);
          
          return (
            <Card key={product.id} className="card-raoul p-4 relative group" data-testid={`product-${product.id}`}>
              {/* Discount Badge */}
              <div className="absolute top-2 right-2 bg-afriperx-green text-white text-xs px-2 py-0.5 rounded-full font-bold">
                30% OFF
              </div>
              
              {/* Product Image */}
              <div className="text-5xl text-center mb-3 h-16 flex items-center justify-center">
                {product.image}
              </div>
              
              {/* Product Info */}
              <h3 className="text-gray-900 font-medium text-sm mb-2 line-clamp-2 h-10">{product.name}</h3>
              
              {/* Pricing */}
              <div className="mb-3">
                <p className="text-gray-400 text-xs line-through">R{product.originalPrice}</p>
                <p className="text-gray-900 font-bold text-lg">R{discountedPrice}</p>
              </div>
              
              {/* Stock */}
              <p className="text-gray-500 text-xs mb-3">{product.stock} in stock</p>
              
              {/* Add to Cart */}
              {inCart ? (
                <div className="flex items-center justify-between bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => updateQuantity(product.id, -1)}
                    className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-200 rounded"
                    data-testid={`decrease-${product.id}`}
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="text-gray-900 font-medium">{inCart.quantity}</span>
                  <button
                    onClick={() => updateQuantity(product.id, 1)}
                    className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-200 rounded"
                    data-testid={`increase-${product.id}`}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <Button
                  onClick={() => addToCart(product)}
                  className="w-full bg-gray-900 text-white hover:bg-gray-800 text-sm py-2"
                  data-testid={`add-to-cart-${product.id}`}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              )}
            </Card>
          );
        })}
      </div>

      {/* Shopping Cart Dialog */}
      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent className="bg-white border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-900 font-syne flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Your Cart ({cartItemCount} items)
            </DialogTitle>
          </DialogHeader>
          
          {cart.length === 0 ? (
            <div className="text-center py-8">
              <ShoppingBag className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Your cart is empty</p>
              <p className="text-gray-400 text-sm">Start shopping to add items</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Cart Items */}
              <div className="max-h-60 overflow-y-auto space-y-3">
                {cart.map((item) => (
                  <div key={item.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className="text-2xl">{item.image}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-900 font-medium text-sm truncate">{item.name}</p>
                      <p className="text-gray-500 text-xs">R{getDiscountedPrice(item.originalPrice)} each</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateQuantity(item.id, -1)}
                        className="w-6 h-6 flex items-center justify-center text-gray-500 hover:bg-gray-200 rounded"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-gray-900 font-medium w-6 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, 1)}
                        className="w-6 h-6 flex items-center justify-center text-gray-500 hover:bg-gray-200 rounded"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="w-6 h-6 flex items-center justify-center text-red-500 hover:bg-red-50 rounded ml-2"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="border-t border-gray-200 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="text-gray-900">R{getCartTotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-green-600">You Save</span>
                  <span className="text-green-600 font-medium">R{getTotalSavings().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
                  <span className="text-gray-900">Total</span>
                  <span className="text-gray-900">R{getCartTotal().toFixed(2)}</span>
                </div>
              </div>

              {/* Balance Check */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Your Balance</span>
                  <span className={`font-medium ${user?.main_balance >= getCartTotal() ? 'text-green-600' : 'text-red-600'}`}>
                    R{user?.main_balance?.toFixed(2)}
                  </span>
                </div>
                {user?.main_balance < getCartTotal() && (
                  <p className="text-red-500 text-xs mt-1">Insufficient funds. Please top up your account.</p>
                )}
              </div>

              {/* Checkout Button */}
              <Button
                onClick={handleCheckout}
                disabled={processing || user?.main_balance < getCartTotal()}
                className="w-full btn-primary"
                data-testid="checkout-btn"
              >
                {processing ? (
                  'Processing...'
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Pay R{getCartTotal().toFixed(2)}
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
